import { FC, Fragment, useEffect, useState } from 'react';
import DynamicSummary from './Summary';
import DynamicTicket from './Flight/DynamicTicket';
import useRecaptcha from 'hooks/useRecaptcha';
import { searchPrebuild } from 'services/dynamic';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DynamicEventResponse, GrossAndTax } from 'ts/dynamic/interfaces/DynamicResponse';
import DetailsDynamicSkeleton from './DetailsDynamicSkeleton';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBed, faPlane, faTicket } from '@fortawesome/free-solid-svg-icons';
import DynamicHotel from './Hotel/DynamicHotel';
import DynamicAttraction from './Attraction/DynamicAttraction';
import Button from 'components/ui/Button/Button';
import { initFlight } from 'utils/dynamic/dynamic-fl';
import { DynamicContext } from 'contexts/DynamicContext';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { useGetPage } from 'hooks/useGetPage';
import { b2b } from 'constants/generic';

const DetailsPredefinedPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleRecapcha, isRecaptchaReady } = useRecaptcha();
  const [urlParams] = useSearchParams();
  const [data, setData] = useState<DynamicEventResponse>();
  const [numberOfPax, setNumberOfPax] = useState(0);
  const [currency, setCurrency] = useState<keyof GrossAndTax>('usd');
  const { pathname } = useLocation();
  useGetPage('Dynamic/Details');

  useEffect(() => {
    if (b2b && pathname !== '/dynamic/predefined') {
      return;
    }
    if (!isRecaptchaReady) return;
    (async () => {
      const token = await handleRecapcha();
      if (!token) return;

      const response = await searchPrebuild(token, +urlParams.get('itemNumber')!);

      if (response) {
        setData(response);

        if (response.dynamicFlight?.selectedFlight) {
          const numberPax = response.dynamicFlight.selectedFlight.flightPricesByPDT.reduce(
            (acc, curr) => curr.quantity + acc,
            0
          );
          setNumberOfPax(numberPax);
        }

        if (response.packageInfo) {
          setCurrency(response.packageInfo.currencyCode.toLowerCase() as keyof GrossAndTax);
        }
      }
    })();
  }, [isRecaptchaReady]); // eslint-disable-line

  const changeToDynamic = () => {
    navigate('/dynamic/details?itemNumber=' + urlParams.get('itemNumber'));
  };

  return (
    <DynamicContext.Provider value={{ currency, setCurrency, numberOfPax, setNumberOfPax }}>
      <div className="details-wrapper">
        {!!data && !data.hasErrors && !data.sessionExpired && (
          <Fragment>
            <Fragment>
              <div className="details">
                {data.dynamicFlight?.selectedFlight && (
                  <div className="default-shadow details-block">
                    <div className="title-header-grey title">
                      <FontAwesomeIcon icon={faPlane} className="mr-5" /> {t('flight.details')}
                    </div>
                    <DynamicTicket
                      dynamicFlight={initFlight(data.dynamicFlight.selectedFlight)}
                      onClickChange={changeToDynamic}
                      btnText={t('change_choice')}
                      mode={0}
                      hideShadow={true}
                    />
                  </div>
                )}

                {data.dynamicHotel && (
                  <div className="default-shadow details-block">
                    <div className="title-header-grey title">
                      <FontAwesomeIcon icon={faBed} className="mr-5" /> {t('hotel.hotel')}
                    </div>
                    <DynamicHotel
                      numberOfPax={numberOfPax}
                      hotel={data.dynamicHotel.selectedHotel}
                      onClickChange={changeToDynamic}
                      selectedRoom={data.dynamicHotel.selectedRoom}
                    />
                  </div>
                )}

                {data.dynamicAttraction && (
                  <div className="default-shadow details-block">
                    <div className="title-header-grey title">
                      <FontAwesomeIcon icon={faTicket} className="mr-5" /> {t('dyn.ticket')}
                    </div>
                    <DynamicAttraction
                      ticket={data.dynamicAttraction}
                      onClickChange={changeToDynamic}
                    />
                  </div>
                )}
                <div className="dyn-details-order">
                  <Button
                    title={t('dyn.to_order')}
                    onClick={changeToDynamic}
                    className="btn btn-lg btn-primary"
                  />
                </div>
              </div>
              <DynamicSummary data={data} />
            </Fragment>
          </Fragment>
        )}
        {!data && <DetailsDynamicSkeleton />}
      </div>
    </DynamicContext.Provider>
  );
};

export default withErrorBoundary(DetailsPredefinedPage, 'DetailsPredefinedPage');
